<template>
  <el-dialog
    :visible.sync="visible"
    :width="dialogWidth"
    :close-on-click-modal="false"
    :show-close="false"
    :before-close="handleBeforeClose"
    :modal="false"
    :modal-append-to-body="false"
    :append-to-body="true"
    custom-class="video-call-dialog vertical-mode"
    top="10vh"
    @mousedown.native="startDrag"
  >
    <div slot="title" class="dialog-title" @mousedown.stop="startDrag">
      <span>视频问诊 — {{ patientInfo.name || '-' }}</span>
      <el-tag size="mini" :type="callStatusType">{{ callStatusText }}</el-tag>
      <i class="el-icon-rank drag-handle" title="拖动" />
    </div>

    <div class="video-wrap vertical">
      <!-- 远端视频 -->
      <div id="trtc-remote-video" class="video-box remote-box">
        <div v-if="!remoteReady" class="video-placeholder">
          <i class="el-icon-video-camera" />
          <span>{{ remotePlaceholderText }}</span>
        </div>
      </div>
      <!-- 本地视频 -->
      <div id="trtc-local-video" class="video-box local-box">
        <div v-if="!localReady" class="video-placeholder small">
          <i class="el-icon-user" />
          <span>本地摄像头</span>
        </div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button type="danger" icon="el-icon-phone" @click="handleHangup">挂断通话</el-button>
      <el-button :type="micMuted ? 'info' : 'primary'" @click="toggleMic">
        {{ micMuted ? '麦克风已关' : '麦克风' }}
      </el-button>
      <el-button :type="cameraOff ? 'info' : 'primary'" @click="toggleCamera">
        {{ cameraOff ? '摄像头已关' : '摄像头' }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import {
  startRemoteView,
  hangupCall,
  openCamera,
  closeCamera,
  openMicrophone,
  closeMicrophone,
  onUserVideoAvailable,
  onCallEnd,
  onCallConnected
} from '@/utils/tuicallHelper'

export default {
  name: 'VideoCallDialog',
  data() {
    return {
      visible: false,
      roomId: '',
      patientInfo: {},
      remoteUserId: '',
      callStatusText: '通话中',
      callStatusType: 'success',
      remoteReady: false,
      localReady: false,
      micMuted: false,
      cameraOff: false,
      remotePlaceholderText: '等待对方接入...',
      listenersBound: false,
      remoteRetryTimer: null,
      callEnded: false,
      // 拖动相关
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      dialogPosition: { x: 0, y: 0 }
    }
  },
  computed: {
    dialogWidth() {
      // 竖屏模式宽度固定，高度自适应
      return '360px'
    }
  },
  beforeDestroy() {
    this.cleanup()
    this.removeDragListeners()
  },
  methods: {
    open(roomId, patientInfo, remoteUserId) {
      if (this.visible) {
        console.warn('[VideoCallDialog] 已有弹窗打开，先关闭旧窗口')
        this.cleanup()
      }

      this.roomId = roomId
      this.patientInfo = patientInfo || {}
      this.remoteUserId = remoteUserId || ''
      this.visible = true
      this.remoteReady = false
      this.localReady = false
      this.micMuted = false
      this.cameraOff = false
      this.remotePlaceholderText = '等待对方接入...'
      this.callEnded = false

      console.log('[VideoCallDialog] open:', { roomId, remoteUserId, patientName: this.patientInfo.name })

      this.$nextTick(() => {
        // 重置位置到右下角
        this.resetPosition()

        // 1. 打开本地摄像头
        openCamera('trtc-local-video').then(() => {
          this.localReady = true
          console.log('[VideoCallDialog] 本地摄像头已打开')
        }).catch(err => {
          console.error('[VideoCallDialog] openCamera 失败:', err)
          this.$message?.warning?.('摄像头打开失败，请检查设备权限')
        })

        // 2. 绑定事件监听（只绑定一次）
        if (!this.listenersBound) {
          this.bindCallListeners()
          this.listenersBound = true
        }

        // 3. 延迟首次尝试订阅远端视频
        if (this.remoteUserId) {
          this.remoteRetryTimer = setTimeout(() => {
            this.tryStartRemoteView(this.remoteUserId, '延迟首次')
          }, 800)
        }
      })
    },

    /** 绑定通话事件监听 */
    bindCallListeners() {
      onCallConnected((event) => {
        console.log('[VideoCallDialog] 通话接通:', event)
        this.callStatusText = '通话中'
        this.callStatusType = 'success'
        if (this.remoteUserId) {
          this.tryStartRemoteView(this.remoteUserId, '通话接通')
        }
      })

      onUserVideoAvailable((event) => {
        const remoteUserId = event.userID || event.userId
        console.log('[VideoCallDialog] 对方视频可用:', event)
        if (remoteUserId) {
          this.remoteUserId = remoteUserId
          this.tryStartRemoteView(remoteUserId, 'USER_VIDEO_AVAILABLE')
        }
      })

      onCallEnd((event) => {
        console.log('[VideoCallDialog] 通话结束事件:', event)
        if (this.callEnded) return
        this.callEnded = true
        this.callStatusText = '通话已结束'
        this.callStatusType = 'info'
        this.remotePlaceholderText = '对方已挂断'
        this.closeDialog()
      })
    },

    tryStartRemoteView(userId, source = 'unknown') {
      if (this.callEnded) return
      console.log(`[VideoCallDialog] [${source}] 尝试订阅远端视频:`, userId)
      startRemoteView(userId, 'trtc-remote-video')
        .then(() => {
          console.log(`[VideoCallDialog] [${source}] 远端视频订阅成功`)
          this.remoteReady = true
        })
        .catch(err => {
          console.warn(`[VideoCallDialog] [${source}] 远端视频订阅失败:`, err)
          if (!this.remoteRetryTimer && !this.callEnded) {
            this.remoteRetryTimer = setTimeout(() => {
              this.tryStartRemoteView(userId, '重试')
            }, 3000)
          }
        })
    },

    async handleHangup() {
      console.log('[VideoCallDialog] 用户主动挂断')
      if (!this.callEnded) {
        try {
          await hangupCall()
        } catch (e) {
          console.warn('[VideoCallDialog] hangupCall 失败（可能对方已挂断）:', e)
        }
      }
      this.closeDialog()
    },

    handleBeforeClose(done) {
      this.handleHangup()
      done()
    },

    closeDialog() {
      console.log('[VideoCallDialog] 关闭弹窗')
      if (this.remoteRetryTimer) {
        clearTimeout(this.remoteRetryTimer)
        this.remoteRetryTimer = null
      }
      this.visible = false
      this.remoteReady = false
      this.localReady = false
      this.micMuted = false
      this.cameraOff = false
      this.remoteUserId = ''
      this.callEnded = false

      try { closeCamera() } catch (e) {}
      try { closeMicrophone() } catch (e) {}
    },

    cleanup() {
      this.closeDialog()
    },

    toggleMic() {
      if (this.micMuted) {
        openMicrophone()
        this.micMuted = false
      } else {
        closeMicrophone()
        this.micMuted = true
      }
    },

    toggleCamera() {
      if (this.cameraOff) {
        openCamera('trtc-local-video').then(() => {
          this.cameraOff = false
        }).catch(err => {
          console.error('[VideoCallDialog] 重新打开摄像头失败:', err)
        })
      } else {
        closeCamera()
        this.cameraOff = true
      }
    },

    // ==================== 拖动功能 ====================
    resetPosition() {
      // 默认定位到右下角
      this.$nextTick(() => {
        const dialog = document.querySelector('.video-call-dialog.vertical-mode')
        if (dialog) {
          const wrap = dialog.closest('.el-dialog__wrapper')
          if (wrap) {
            wrap.style.position = 'fixed'
            wrap.style.left = 'auto'
            wrap.style.top = 'auto'
            wrap.style.right = '20px'
            wrap.style.bottom = '20px'
            wrap.style.width = 'auto'
            wrap.style.height = 'auto'
            wrap.style.overflow = 'visible'
          }
          dialog.style.margin = '0'
          dialog.style.position = 'relative'
        }
      })
    },

    startDrag(e) {
      if (!this.visible) return
      const dialog = document.querySelector('.video-call-dialog.vertical-mode')
      const wrap = dialog?.closest('.el-dialog__wrapper')
      if (!wrap) return

      this.isDragging = true
      const rect = wrap.getBoundingClientRect()
      this.dragOffset.x = e.clientX - rect.left
      this.dragOffset.y = e.clientY - rect.top

      wrap.style.left = rect.left + 'px'
      wrap.style.top = rect.top + 'px'
      wrap.style.right = 'auto'
      wrap.style.bottom = 'auto'

      document.addEventListener('mousemove', this.onDragMove)
      document.addEventListener('mouseup', this.onDragEnd)
    },

    onDragMove(e) {
      if (!this.isDragging) return
      const wrap = document.querySelector('.video-call-dialog.vertical-mode')?.closest('.el-dialog__wrapper')
      if (!wrap) return

      let x = e.clientX - this.dragOffset.x
      let y = e.clientY - this.dragOffset.y

      // 限制在视口内
      const maxX = window.innerWidth - wrap.offsetWidth
      const maxY = window.innerHeight - wrap.offsetHeight
      x = Math.max(0, Math.min(x, maxX))
      y = Math.max(0, Math.min(y, maxY))

      wrap.style.left = x + 'px'
      wrap.style.top = y + 'px'
    },

    onDragEnd() {
      this.isDragging = false
      this.removeDragListeners()
    },

    removeDragListeners() {
      document.removeEventListener('mousemove', this.onDragMove)
      document.removeEventListener('mouseup', this.onDragEnd)
    }
  }
}
</script>

<style scoped lang="scss">
/* 竖屏弹窗样式 */
.video-call-dialog.vertical-mode {
  margin: 0 !important;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);

  ::v-deep .el-dialog {
    width: 360px !important;
    margin: 0 !important;
    border-radius: 12px;
    overflow: hidden;
  }

  ::v-deep .el-dialog__header {
    padding: 12px 16px;
    background: #1d2129;
    border-bottom: 1px solid #333;
    cursor: move;
    user-select: none;
  }

  ::v-deep .el-dialog__body {
    padding: 0;
    background: #1d2129;
  }

  ::v-deep .el-dialog__footer {
    padding: 12px 16px;
    background: #1d2129;
    border-top: 1px solid #333;
  }
}

.dialog-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 14px;
  color: #fff;

  .drag-handle {
    margin-left: auto;
    color: #909399;
    font-size: 16px;
    cursor: grab;
    &:active {
      cursor: grabbing;
    }
  }
}

/* 竖屏视频布局 */
.video-wrap.vertical {
  position: relative;
  width: 360px;
  height: 480px;
  background: #000;
  overflow: hidden;

  .video-box {
    position: absolute;
    background: #000;
    overflow: hidden;

    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  /* 远端视频 - 竖屏全屏背景 */
  .remote-box {
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 1;
  }

  /* 本地视频 - 竖屏右上角小窗 */
  .local-box {
    width: 120px;
    height: 160px;
    top: 12px;
    right: 12px;
    z-index: 2;
    border-radius: 8px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    background: #333;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  .video-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 14px;

    i {
      font-size: 48px;
      margin-bottom: 12px;
      color: #909399;
    }

    &.small {
      i {
        font-size: 28px;
        margin-bottom: 6px;
      }
      span {
        font-size: 11px;
      }
    }
  }
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 8px;

  .el-button {
    padding: 8px 12px;
    font-size: 12px;
  }
}

/* 覆盖 el-dialog 默认居中，支持自由定位 */
::v-deep .el-dialog__wrapper {
  position: fixed !important;
  overflow: visible !important;
}

::v-deep .el-dialog.vertical-mode {
  position: relative !important;
  margin: 0 !important;
}
</style>
